// @ts-strict-ignore
import { useMutation, useQuery } from "@apollo/client";
import useNavigator from "@dashboard/hooks/useNavigator";
import { Box, Button, Dropdown, Text } from "@saleor/macaw-ui-next";
import { Bell } from "lucide-react";
import { useEffect, useState } from "react";

import { registerPush } from "./firebasePush";
import {
  NOTIFICATION_MARK_ALL_READ_MUTATION,
  NOTIFICATION_MARK_READ_MUTATION,
  PUSH_DEVICE_REGISTER_MUTATION,
} from "./mutations";
import {
  NOTIFICATIONS_QUERY,
  UNREAD_NOTIFICATION_COUNT_QUERY,
} from "./queries";

interface NotificationItem {
  id: string;
  type: string;
  title: string;
  message: string | null;
  link: string | null;
  isRead: boolean;
  createdAt: string;
}

const formatWhen = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-IN", { month: "short", day: "numeric" });
};

export const NotificationBell = () => {
  const navigate = useNavigator();
  const [open, setOpen] = useState(false);

  const { data: countData, refetch: refetchCount } = useQuery(
    UNREAD_NOTIFICATION_COUNT_QUERY,
    { fetchPolicy: "cache-and-network", pollInterval: 60000 },
  );
  const unread: number = countData?.myUnreadNotificationCount ?? 0;

  const { data: listData, loading, refetch: refetchList } = useQuery(
    NOTIFICATIONS_QUERY,
    { variables: { first: 15 }, fetchPolicy: "cache-and-network", skip: !open },
  );
  const items: NotificationItem[] = listData?.myNotifications ?? [];

  const [markRead] = useMutation(NOTIFICATION_MARK_READ_MUTATION);
  const [markAllRead] = useMutation(NOTIFICATION_MARK_ALL_READ_MUTATION);
  const [registerDevice] = useMutation(PUSH_DEVICE_REGISTER_MUTATION);

  // Register this browser for Web Push (best-effort; prompts once per load).
  useEffect(() => {
    registerPush(token => registerDevice({ variables: { token } }));
  }, [registerDevice]);

  const handleItemClick = async (item: NotificationItem) => {
    setOpen(false);
    if (!item.isRead) {
      await markRead({ variables: { id: item.id } });
      refetchCount();
      refetchList();
    }
    if (item.link && item.link.startsWith("/")) navigate(item.link);
  };

  const handleMarkAll = async () => {
    await markAllRead();
    refetchCount();
    refetchList();
  };

  return (
    <Dropdown open={open} onOpenChange={setOpen}>
      <Dropdown.Trigger>
        <Box position="relative" display="flex">
          <Button
            variant="tertiary"
            size="medium"
            icon={<Bell />}
            data-test-id="notification-bell"
            onClick={() => setOpen(true)}
          />
          {unread > 0 && (
            <Box
              position="absolute"
              display="flex"
              alignItems="center"
              justifyContent="center"
              __top="0px"
              __right="0px"
              __minWidth="16px"
              __height="16px"
              paddingX={0.5}
              __borderRadius="50%"
              __backgroundColor="#DC2626"
            >
              <Text size={1} __color="#FFFFFF" fontWeight="bold">
                {unread > 99 ? "99+" : unread}
              </Text>
            </Box>
          )}
        </Box>
      </Dropdown.Trigger>
      <Dropdown.Content align="end">
        <Box
          __width={340}
          backgroundColor="default1"
          borderRadius={4}
          boxShadow="defaultOverlay"
          __overflow="hidden"
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            paddingX={4}
            paddingY={3}
            borderBottomWidth={1}
            borderBottomStyle="solid"
            borderColor="default1"
          >
            <Text size={3} fontWeight="bold">
              Notifications
            </Text>
            {unread > 0 && (
              <Button variant="tertiary" size="small" onClick={handleMarkAll}>
                Mark all read
              </Button>
            )}
          </Box>

          <Box __maxHeight={400} __overflowY="auto">
            {loading && items.length === 0 ? (
              <Box padding={5}>
                <Text size={2} color="default2">
                  Loading…
                </Text>
              </Box>
            ) : items.length === 0 ? (
              <Box padding={5}>
                <Text size={2} color="default2">
                  You're all caught up.
                </Text>
              </Box>
            ) : (
              items.map(item => (
                <Box
                  key={item.id}
                  cursor="pointer"
                  paddingX={4}
                  paddingY={3}
                  borderBottomWidth={1}
                  borderBottomStyle="solid"
                  borderColor="default1"
                  backgroundColor={{
                    default: item.isRead ? "default1" : "default2",
                    hover: "default2",
                  }}
                  onClick={() => handleItemClick(item)}
                >
                  <Box display="flex" justifyContent="space-between" gap={2}>
                    <Text size={2} fontWeight={item.isRead ? "medium" : "bold"}>
                      {item.title}
                    </Text>
                    <Text size={1} color="default2">
                      {formatWhen(item.createdAt)}
                    </Text>
                  </Box>
                  {item.message && (
                    <Text size={2} color="default2">
                      {item.message}
                    </Text>
                  )}
                </Box>
              ))
            )}
          </Box>
        </Box>
      </Dropdown.Content>
    </Dropdown>
  );
};
